import React, { useEffect, useState } from "react";
import { Container as ContainerBase } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Dialog } from 'primereact/dialog';
import { createJob, updateJob } from "api/jobs";

const Container = tw(ContainerBase)`min-h-screen bg-gray-100 text-gray-900 font-medium flex justify-center items-center`;
const Content = tw.div`bg-white rounded-2xl shadow-lg w-full max-w-2xl p-8`;
const Heading = tw.h1`text-3xl font-bold mb-6 text-center text-pbsGreen-900`;
const Form = tw.form`space-y-5`;
const Input = tw.input`w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-pbsGreen-500 focus:bg-white`;
const TextArea = tw.textarea`w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-pbsGreen-500 focus:bg-white`;

const Label = tw.label`block text-sm font-semibold text-gray-700`;
const Field = tw.div`space-y-2`;
const ErrorMessage = tw.p`text-red-600 text-sm text-center`;

const SubmitButton = styled.button`
  ${tw`w-full py-3 rounded-md bg-pbsGreen-500 text-white font-semibold text-sm hover:bg-pbsGreen-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
`;

const emptyForm = { title: '', pay: '', parish: '', description: '', imageUrl: '' };

export default function JobFormModal({ visible, setVisible, job = null, onSaved }) {
  const isEditMode = Boolean(job && job.id);

  const [title, setTitle] = useState(emptyForm.title);
  const [pay, setPay] = useState(emptyForm.pay);
  const [parish, setParish] = useState(emptyForm.parish);
  const [description, setDescription] = useState(emptyForm.description);
  const [imageUrl, setImageUrl] = useState(emptyForm.imageUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Populate the form when opening in edit mode, or reset it when opening in create mode
  useEffect(() => {
    if (!visible) return;

    setErrorMessage('');

    if (isEditMode) {
      setTitle(job.title || '');
      setPay(job.pay ?? '');
      setParish(job.parish || '');
      setDescription(job.description || '');
      setImageUrl(job.image_url || '');
    } else {
      setTitle(emptyForm.title);
      setPay(emptyForm.pay);
      setParish(emptyForm.parish);
      setDescription(emptyForm.description);
      setImageUrl(emptyForm.imageUrl);
    }
  }, [visible, job, isEditMode]);

  const handleClose = () => {
    if (isSubmitting) return;
    setVisible(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    const payload = { title, pay, parish, description, image_url: imageUrl };
    const request = isEditMode
      ? updateJob({ id: job.id, ...payload })
      : createJob(payload);

    request
      .then((response) => {
        if (response && response.success === false) {
          setErrorMessage(
            response.message || (isEditMode ? 'Failed to update job.' : 'Failed to create job.')
          );
          return;
        }
        console.log(isEditMode ? 'Job updated successfully:' : 'Job created successfully:', response);
        setVisible(false);
        if (onSaved) onSaved();
      })
      .catch((error) => {
        console.error(isEditMode ? 'Error updating job:' : 'Error creating job:', error);
        setErrorMessage(
          isEditMode
            ? 'Failed to update the job. Please try again.'
            : 'Failed to create the job. Please try again.'
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Dialog
      visible={visible}
      modal
      dismissableMask
      onHide={handleClose}
      breakpoints={{ '960px': '90vw' }}
      style={{ width: '50vw' }}
      contentStyle={{ padding: 0, borderRadius: '1rem', maxHeight: '90vh', overflowY: 'auto' }}
      maskClassName="bg-black bg-opacity-50"
    >
      <div className="overflow-y-auto max-h-[85vh]">
        <Container>
          <Content>
            <Heading>{isEditMode ? 'Edit Job' : 'Create Job'}</Heading>
            <Form onSubmit={handleSubmit}>
              <Field>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  type="text"
                  name="image_url"
                  id="image_url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="title">Title</Label>
                <Input
                  type="text"
                  name="title"
                  id="title"
                  placeholder="Job Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="parish">Parish</Label>
                <Input
                  type="text"
                  name="parish"
                  id="parish"
                  placeholder="Kingston, St. Andrew, etc."
                  value={parish}
                  onChange={(e) => setParish(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="pay">Pay</Label>
                <Input
                  type="number"
                  name="pay"
                  id="pay"
                  placeholder="e.g. 5000"
                  value={pay}
                  onChange={(e) => setPay(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="description">Description</Label>
                <TextArea
                  name="description"
                  id="description"
                  placeholder="Job description goes here..."
                  rows="4"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (isEditMode ? 'Saving...' : 'Submitting...') : (isEditMode ? 'Save Changes' : 'Submit')}
              </SubmitButton>
            </Form>
          </Content>
        </Container>
      </div>
    </Dialog>
  );
}
